import { useMemo } from 'react';
import { usePlateEditor, createPlatePlugin } from 'platejs/react';
import { PlaywrightPlugin } from '@platejs/playwright';
import { markOp } from '../debug/platePerf';
import {
    BoldPlugin,
    ItalicPlugin,
    UnderlinePlugin,
    StrikethroughPlugin,
    CodePlugin,
    H1Plugin,
    H2Plugin,
    H3Plugin,
    BlockquotePlugin,
    HighlightPlugin,
    KbdPlugin,
    SubscriptPlugin,
    SuperscriptPlugin,
    HorizontalRulePlugin,
} from '@platejs/basic-nodes/react';
import { CodeBlockPlugin, CodeLinePlugin, CodeSyntaxPlugin } from '@platejs/code-block/react';
import { createLowlight } from 'lowlight';
import javascript from 'highlight.js/lib/languages/javascript';
import typescript from 'highlight.js/lib/languages/typescript';
import python from 'highlight.js/lib/languages/python';
import json from 'highlight.js/lib/languages/json';
import bash from 'highlight.js/lib/languages/bash';
import sql from 'highlight.js/lib/languages/sql';
import css from 'highlight.js/lib/languages/css';
import xml from 'highlight.js/lib/languages/xml';
import yaml from 'highlight.js/lib/languages/yaml';
import java from 'highlight.js/lib/languages/java';
import go from 'highlight.js/lib/languages/go';
import plaintext from 'highlight.js/lib/languages/plaintext';
import { CodeBlockElement, CodeLineElement } from '../components/CodeBlockElement/CodeBlockElement';

// Lowlight instance with curated languages only (bundle-size conscious).
// jsx/tsx are aliases of javascript/typescript in highlight.js.
const lowlight = createLowlight();
lowlight.register({ javascript, typescript, python, json, bash, sql, css, xml, yaml, java, go, plaintext });
// Aliases so the language selector values 'jsx'/'tsx'/'shell' resolve correctly.
lowlight.registerAlias({ javascript: ['jsx'], typescript: ['tsx'], bash: ['shell', 'sh'] });
import { CalloutPlugin } from '@platejs/callout/react';
import { ColumnPlugin, ColumnItemPlugin } from '@platejs/layout/react';
import {
    FontColorPlugin,
    FontFamilyPlugin,
    FontSizePlugin,
    FontBackgroundColorPlugin,
    TextAlignPlugin,
    LineHeightPlugin,
} from '@platejs/basic-styles/react';
import { TablePlugin, TableRowPlugin, TableCellPlugin, TableCellHeaderPlugin } from '@platejs/table/react';
import { IndentPlugin } from '@platejs/indent/react';
import { ListPlugin } from '@platejs/list/react';
import { BulletedListRules, OrderedListRules } from '@platejs/list';
import {
    BoldRules,
    ItalicRules,
    CodeRules,
    StrikethroughRules,
    BlockquoteRules,
    HeadingRules,
    HorizontalRuleRules,
} from '@platejs/basic-nodes';
import { LinkPlugin } from '@platejs/link/react';
import { MentionPlugin, MentionInputPlugin } from '@platejs/mention/react';
import { SlashPlugin, SlashInputPlugin } from '@platejs/slash-command/react';
import { EmojiPlugin, EmojiInputPlugin } from '@platejs/emoji/react';
import { DndPlugin } from '@platejs/dnd';
import { TocPlugin } from '@platejs/toc/react';
import { TrailingBlockPlugin, ExitBreakPlugin, NodeIdPlugin, KEYS } from 'platejs';
import { FindReplacePlugin } from '@platejs/find-replace';
import { MarkdownPlugin } from '@platejs/markdown';
import { sanitizePlateFragment } from '../serializers/sanitizePlateFragment';
import type { Value } from 'platejs';
import { SearchHighlightLeaf } from '../findReplace/SearchHighlightLeaf';
import { BlockDraggable } from '../components/BlockDraggable/BlockDraggable';
import { EMPTY_EDITOR_VALUE } from '../types/types';
import { TableElement, TableRowElement, TableCellElement, TableCellHeaderElement } from '../components/TableElements/tableElements';
import { CELL_SELECTION_OPTION } from '../components/TableElements/cellSelection';
import { generateCellId } from '../components/TableElements/tableElementsUtils';
import { findInterVoidInsertIndex, emptyParagraph } from './interVoidParagraph';
import { shouldPreventTableBoundaryMerge } from './preventTableBoundaryMerge';
import { BlockList } from '../components/BlockList/BlockList';
import { LinkElement } from '../components/LinkElement/LinkElement';
import { ImagePlugin } from '../components/ImagePlugin/ImagePlugin';
import { HrElement } from '../components/HrElement/HrElement';
import { ColumnGroupElement } from '../components/ColumnGroupElement/ColumnGroupElement';
import { ColumnElement } from '../components/ColumnElement/ColumnElement';
import { MentionElement } from '../components/MentionElement/MentionElement';
import { MentionInputElement } from '../components/MentionElement/MentionInputElement';
import { SlashInputElement } from '../components/SlashElement/SlashInputElement';
import { EmojiInputElement } from '../components/EmojiElement/EmojiInputElement';
import { TocElement } from '../components/TocElement/TocElement';

interface UsePlateEditorLogicArgs {
    initialValue?: Value;
    /**
     * When non-empty, registers the @platejs/mention plugin. The actual
     * list is consumed by MentionInputElement via PlateEditorConfigContext —
     * passing only the toggle here so the editor's plugin set is stable
     * across re-renders (changing plugins recreates the editor).
     */
    enableMentions?: boolean;
    /**
     * When true, registers NodeIdPlugin + DndPlugin for block drag-and-drop.
     * Default false — see PlateEditorProps.enableBlockDnd for the rationale
     * (co-mount crash + PDF export corruption). Constant per mount.
     */
    enableBlockDnd?: boolean;
}

const INLINE_STYLE_TARGETS: string[] = [
    ...((KEYS as any).heading ?? []),
    (KEYS as any).p,
    (KEYS as any).blockquote,
    (KEYS as any).td,
    (KEYS as any).th,
    (KEYS as any).callout,
].filter(Boolean);

// Excludes td/th — putting them here causes double-bullet rendering inside cells.
const LIST_INDENT_TARGETS: string[] = [
    ...((KEYS as any).heading ?? []),
    (KEYS as any).p,
    (KEYS as any).blockquote,
    (KEYS as any).callout,
].filter(Boolean);

// Paragraph space-before/after (OOXML docx import — see ooxml/applyOoxmlProps.ts)
// has no built-in Plate style plugin; inject it the same way LineHeightPlugin
// injects lineHeight, mapping the node's px value to a CSS margin.
const SpacingBeforePlugin = createPlatePlugin({
    key: 'spacingBefore',
    inject: {
        targetPlugins: INLINE_STYLE_TARGETS,
        nodeProps: { nodeKey: 'spacingBefore', styleKey: 'marginTop', transformNodeValue: ({ nodeValue }: any) => `${nodeValue}px` },
    },
});
const SpacingAfterPlugin = createPlatePlugin({
    key: 'spacingAfter',
    inject: {
        targetPlugins: INLINE_STYLE_TARGETS,
        nodeProps: { nodeKey: 'spacingAfter', styleKey: 'marginBottom', transformNodeValue: ({ nodeValue }: any) => `${nodeValue}px` },
    },
});

/**
 * Defensive sanitizer for Plate initial values.
 *
 * Delegates to `sanitizePlateFragment` — the shared ingress sanitizer — so
 * init, paste, import, and AI-output paths all use the same rules. See
 * `sanitizePlateFragment.ts` for the full A1 taxonomy that is enforced.
 *
 * Kept as a named wrapper so call sites in this file stay readable and the
 * stray-newline-spacer behaviour (embedded in sanitizePlateFragment) is
 * preserved without duplication.
 */
function sanitizePlateValue(nodes: any): any[] {
    return sanitizePlateFragment(nodes);
}

/**
 * `overrideEditor` factory (shared by TableCellPlugin + TableCellHeaderPlugin)
 * that stamps a stable `id` on any td/th missing one. @platejs/table caches its
 * merged-cell index matrix by `cell.id`; without ids merged cells are
 * unselectable and the index cache is defeated (see generateCellId). Assigning
 * only on cells (not every block via NodeIdPlugin) keeps the saved doc lean per
 * the "emit props only when needed" rule. `type` is the plugin's own node type
 * ('td' / 'th'), so the same factory works for both.
 */
const withCellIdNormalizer = ({ editor, tf: { normalizeNode }, type }: any) => ({
    transforms: {
        normalizeNode([n, path]: any) {
            if (n?.type === type && !n.id) {
                (editor as any).tf.setNodes({ id: generateCellId() }, { at: path });
                return;
            }
            return normalizeNode([n, path]);
        },
    },
});

/**
 * The editor's plugin array, extracted from the hook so tests (and any
 * headless `createPlateEditor`) can instantiate an editor with the EXACT
 * production plugin configuration — list normalizers, indent offset, parse
 * rules and all. A hand-rolled subset in a test could pass while prod stays
 * broken, so the paste-pipeline tests build their editor from this factory.
 * Behavior-preserving: the hook below passes the result straight through to
 * `usePlateEditor`, which forwards it to `createPlateEditor`.
 */
export function createPlateEditorPlugins({
    enableMentions = false,
    enableBlockDnd = false,
}: { enableMentions?: boolean; enableBlockDnd?: boolean } = {}) {
    return [
        PlaywrightPlugin.configure({ enabled: process.env.NODE_ENV !== 'production' }),
        // Dev-only op tracer: a performance.mark per editor operation when
        // PLATE_PERF is on (see debug/platePerf.ts). Registration is gated on
        // NODE_ENV so prod builds drop it entirely.
        ...(process.env.NODE_ENV !== 'production'
            ? [
                  createPlatePlugin({ key: 'platePerfMarks' }).overrideEditor(({ tf: { apply } }: any) => ({
                      transforms: {
                          apply(op: any) {
                              markOp(op?.type);
                              apply(op);
                          },
                      },
                  })) as any,
              ]
            : []),
        // Block drag-and-drop is opt-in (enableBlockDnd). When OFF (default)
        // neither NodeIdPlugin nor DndPlugin is registered, so blocks get no
        // `id` and no <BlockDraggable> wrapper — the editor DOM is identical to
        // the pre-feature shape. This avoids the co-mount "two HTML5 backends"
        // crash and keeps the PDF exporters' DOM walk (which keys off
        // data-slate-node) from tripping over the wrapper div. When ON, both
        // plugins register and PlateEditor mounts the matching <DndProvider>.
        ...(enableBlockDnd
            ? [
                  // NodeIdPlugin assigns a stable `id` to every block (filtering
                  // inline + text by default), reusing existing ids and de-duping.
                  // @platejs/dnd identifies the drag source / drop target by that
                  // id, so this MUST be registered for block reordering to work.
                  // Registered before DndPlugin.
                  NodeIdPlugin as any,
                  // Block drag-and-drop. `render.aboveNodes` wraps every element in
                  // <BlockDraggable>, which paints the gutter handle + drop-line for
                  // drag-safe top-level blocks (and renders children untouched for
                  // tables/columns/read-only). The editor tree is wrapped in
                  // <DndProvider backend={HTML5Backend}> in PlateEditor.tsx —
                  // react-dnd requires that provider for the drag hooks to function.
                  (DndPlugin as any).configure({
                      render: {
                          aboveNodes:
                              () =>
                              ({ element, children }: any) => <BlockDraggable element={element}>{children}</BlockDraggable>,
                      },
                  }),
              ]
            : []),
        ...(enableMentions
            ? [
                  // @platejs/mention configured with `insertSpaceAfterMention` to
                  // match the existing comments UX (mention + trailing
                  // space). The combobox UI lives in MentionInputElement, which
                  // reads the user list from PlateEditorConfigContext.
                  (MentionPlugin as any).configure({ options: { insertSpaceAfterMention: true } }).withComponent(MentionElement),
                  (MentionInputPlugin as any).withComponent(MentionInputElement),
              ]
            : []),
        // Slash command menu: `/` at block start (or after whitespace) opens a
        // filterable popup to insert/convert a block. Registered unconditionally
        // like TrailingBlockPlugin — works in all editor contexts (one-pager,
        // compose-email, comments). triggerPreviousCharPattern is the default
        // /^\s?$/ from BaseSlashPlugin so it won't fire mid-URL.
        SlashPlugin as any,
        (SlashInputPlugin as any).withComponent(SlashInputElement),
        // Emoji picker: `:query` at a word boundary opens a filterable inline
        // combobox. triggerPreviousCharPattern: /^\s?$/ (set by BaseEmojiPlugin)
        // — fires only at block-start or after whitespace, so URLs (`https://`),
        // times (`10:30`), and ratios (`1:2`) never trigger. @emoji-mart/data is
        // lazy-loaded inside EmojiInputElement on first open — not bundled here.
        EmojiPlugin as any,
        (EmojiInputPlugin as any).withComponent(EmojiInputElement),
        // Mark plugins — inputRules add markdown shortcuts:
        //   **text** → bold, *text* → italic, `text` → code, ~~text~~ → strike.
        // Asterisk-only: the `_single_` / `__dunder__` underscore variants would
        // auto-italicize snake_case API fields and Python identifiers — common in
        // business/technical content — so they are deliberately omitted.
        // Lists excluded (owned by ListPlugin.inputRules below).
        // Smart-quotes / legal / arrow shortcuts excluded intentionally.
        (BoldPlugin as any).configure({ inputRules: [BoldRules.markdown({ variant: '*' })] }),
        (ItalicPlugin as any).configure({ inputRules: [ItalicRules.markdown({ variant: '*' })] }),
        UnderlinePlugin,
        (StrikethroughPlugin as any).configure({ inputRules: [StrikethroughRules.markdown()] }),
        (CodePlugin as any).configure({ inputRules: [CodeRules.markdown()] }),
        HighlightPlugin,
        KbdPlugin,
        SubscriptPlugin,
        SuperscriptPlugin,
        // Heading plugins default to rules.delete.start='reset' (Notion-style:
        // Backspace at the start of a heading silently downgrades it to a
        // paragraph in place). Override to 'default' so Backspace-at-start
        // merges into the previous block instead — matching Word/Google Docs
        // and the legacy TextEditor. break.splitReset / merge.removeEmpty are
        // preserved (rules deep-merge). HeadingRules.markdown() uses pluginKey
        // internally (matches /^h([1-6])$/) so each level must be registered
        // on its own plugin configure call.
        (H1Plugin as any).configure({ rules: { delete: { start: 'default' } }, inputRules: [HeadingRules.markdown()] }),
        (H2Plugin as any).configure({ rules: { delete: { start: 'default' } }, inputRules: [HeadingRules.markdown()] }),
        (H3Plugin as any).configure({ rules: { delete: { start: 'default' } }, inputRules: [HeadingRules.markdown()] }),
        (BlockquotePlugin as any).configure({ inputRules: [BlockquoteRules.markdown()] }),
        // HrElement wraps <hr/> to avoid React's "void element with children" error.
        // HorizontalRuleRules variant '-' fires on '--' or '—'; variant '_' fires on '___'.
        (HorizontalRulePlugin as any)
            .configure({ inputRules: [HorizontalRuleRules.markdown({ variant: '-' }), HorizontalRuleRules.markdown({ variant: '_' })] })
            .withComponent(HrElement),
        CalloutPlugin,
        (FontColorPlugin as any).configure({ inject: { targetPlugins: INLINE_STYLE_TARGETS } }),
        (FontBackgroundColorPlugin as any).configure({ inject: { targetPlugins: INLINE_STYLE_TARGETS } }),
        (FontSizePlugin as any).configure({ inject: { targetPlugins: INLINE_STYLE_TARGETS } }),
        (FontFamilyPlugin as any).configure({ inject: { targetPlugins: INLINE_STYLE_TARGETS } }),
        (TextAlignPlugin as any).configure({ inject: { targetPlugins: [...INLINE_STYLE_TARGETS, 'image'] } }),
        // Block-level line-height — drives the LineHeightMenu toolbar.
        // Defaults to 1.2 to match the tight list rhythm in .content; user
        // can override per-block via the dropdown.
        (LineHeightPlugin as any).configure({
            inject: { targetPlugins: INLINE_STYLE_TARGETS, nodeProps: { defaultNodeValue: 1.2 } },
        }),
        SpacingBeforePlugin as any,
        SpacingAfterPlugin as any,
        (LinkPlugin as any).withComponent(LinkElement),
        (IndentPlugin as any).configure({ inject: { targetPlugins: LIST_INDENT_TARGETS }, options: { offset: 24 } }),
        // Two normalizers, both required to kill a double-bullet bug in table
        // cells: (1) strips stray listStyleType/listStart landing on a container
        // node (td/th/table/etc.) — BlockList would otherwise double-wrap it.
        // (2) lifts nested list <p>s out of a list-style parent <p> — legacy docs
        // from the old slate→plate converter nest them as CHILDREN instead of
        // SIBLINGS, so BlockList wraps both the outer and inner <p> in <ul><li>.
        (ListPlugin as any)
            .configure({
                inject: { targetPlugins: LIST_INDENT_TARGETS },
                render: { belowNodes: BlockList },
                // Markdown-style input rules: `1. ` / `1) ` → numbered
                // list, `- ` / `* ` → bullet list, fired on a trailing
                // space at the start of a block.
                inputRules: [
                    OrderedListRules.markdown({ variant: '.' }),
                    OrderedListRules.markdown({ variant: ')' }),
                    BulletedListRules.markdown({ variant: '-' }),
                    BulletedListRules.markdown({ variant: '*' }),
                ],
            })
            .overrideEditor(({ editor: ed, tf: { normalizeNode } }: any) => ({
                transforms: {
                    normalizeNode([n, path]: any) {
                        // Text leaves have no `type` — all three list checks below require
                        // n.type, so they'd all fall through to the delegate call anyway.
                        // Return early to avoid traversing LIST_INDENT_TARGETS on every leaf.
                        if (!n?.type) return normalizeNode([n, path]);
                        // (1) Strip stray list props from non-target node types.
                        if (n?.type && !LIST_INDENT_TARGETS.includes(n.type)) {
                            if (n.listStyleType != null || n.listStart != null || n.listRestart != null) {
                                ed.tf.unsetNodes(['listStyleType', 'listStart', 'listRestart'], { at: path });
                                return;
                            }
                        }
                        // (1b) toggleList flags every block in the selection, including
                        // empty separator <p>s legacy cells use for spacing — those become
                        // stray bulleted rows. Delete an empty list-style <p> ONLY when
                        // BOTH neighbors are real list items (a true separator artifact);
                        // an OR check here also deleted the fresh empty <p> Enter creates
                        // mid-list, making the bullet vanish on keypress.
                        if (
                            n?.type &&
                            LIST_INDENT_TARGETS.includes(n.type) &&
                            n.listStyleType != null &&
                            Array.isArray(n.children) &&
                            n.children.every((c: any) => c && typeof c === 'object' && typeof c.text === 'string' && c.text.trim() === '')
                        ) {
                            // Skip when the empty block IS the current
                            // selection target — pressing Enter in the
                            // middle of a list creates exactly this shape
                            // (empty new item flanked by two real items),
                            // and deleting it makes Enter feel like it
                            // jumped into the next item instead of creating
                            // a new one.
                            const selAnchor = ed.selection?.anchor?.path;
                            const isCaretInside =
                                Array.isArray(selAnchor) &&
                                selAnchor.length >= path.length &&
                                path.every((seg: number, i: number) => seg === selAnchor[i]);
                            if (isCaretInside) {
                                return normalizeNode([n, path]);
                            }
                            const parentEntry = ed.api?.parent?.(path);
                            const parent = parentEntry?.[0];
                            if (parent && Array.isArray(parent.children)) {
                                const idx = path[path.length - 1];
                                const hasNext = idx + 1 < parent.children.length;
                                if (hasNext) {
                                    const isRealListItem = (sib: any) =>
                                        sib &&
                                        sib.listStyleType === n.listStyleType &&
                                        Array.isArray(sib.children) &&
                                        sib.children.some(
                                            (c: any) => c && typeof c === 'object' && typeof c.text === 'string' && c.text.trim() !== '',
                                        );
                                    const prev = idx > 0 ? parent.children[idx - 1] : null;
                                    const next = parent.children[idx + 1];
                                    if (isRealListItem(prev) && isRealListItem(next)) {
                                        ed.tf.removeNodes({ at: path });
                                        return;
                                    }
                                }
                            }
                        }
                        // (2) Lift block children out of a list-style <p> as siblings —
                        // <p> shouldn't hold block children in Plate's data model, and
                        // legacy Slate docs nest them here (see normalizer comment above).
                        // Empty separator <p>s are DROPPED rather than lifted; lifting
                        // them would produce visible empty bullet lines.
                        if (n?.type === 'p' && n.listStyleType && Array.isArray(n.children)) {
                            // Identify block-child indices. Skip inline link
                            // nodes ('a') — those belong inside the paragraph.
                            const blockIdx: number[] = [];
                            for (let i = 0; i < n.children.length; i++) {
                                const c = n.children[i];
                                if (c && typeof c === 'object' && c.type && c.type !== 'a') blockIdx.push(i);
                            }
                            if (blockIdx.length > 0) {
                                const parentEntry = ed.api?.parent?.(path);
                                if (parentEntry) {
                                    const parentPath = parentEntry[1];
                                    const indexInParent = path[path.length - 1];
                                    const isBlockEmpty = (b: any): boolean => {
                                        if (!b || !Array.isArray(b.children) || b.children.length === 0) return true;
                                        return b.children.every(
                                            (c: any) => c && typeof c === 'object' && typeof c.text === 'string' && c.text.trim() === '',
                                        );
                                    };
                                    // Snapshot block children (source order) and partition
                                    // into "real" (lift to siblings) and "empty" (drop).
                                    const realBlocks: any[] = [];
                                    for (const i of blockIdx) {
                                        const child = n.children[i];
                                        if (!isBlockEmpty(child)) realBlocks.push({ ...child });
                                    }
                                    const parentIndent = (n.indent ?? 1) as number;
                                    const parentListStyleType = n.listStyleType;
                                    ed.tf.withoutNormalizing(() => {
                                        // Remove ALL block children from the parent (both
                                        // empty and real) in reverse order so earlier indices
                                        // stay valid as we delete.
                                        for (let k = blockIdx.length - 1; k >= 0; k--) {
                                            ed.tf.removeNodes({ at: [...path, blockIdx[k]] });
                                        }
                                        // Insert only the REAL (non-empty) lifted blocks as
                                        // siblings after the parent. Each inherits
                                        // listStyleType (if missing) and bumps indent so
                                        // visual nesting is preserved.
                                        realBlocks.forEach((b, i) => {
                                            const sibling: any = { ...b };
                                            if (sibling.listStyleType == null) sibling.listStyleType = parentListStyleType;
                                            if (sibling.indent == null) sibling.indent = parentIndent + 1;
                                            ed.tf.insertNodes(sibling, { at: [...parentPath, indexInParent + 1 + i] });
                                        });
                                    });
                                    return;
                                }
                            }
                        }
                        return normalizeNode([n, path]);
                    },
                },
            })),
        ImagePlugin,
        // Flatten nested column_groups to prevent broken layouts.
        (ColumnPlugin as any)
            .overrideEditor(({ editor, tf: { normalizeNode }, type }: any) => ({
                transforms: {
                    normalizeNode([n, path]: any) {
                        // Body only acts when n.type === type (column_group). Skip everything else.
                        if (n?.type !== type) return normalizeNode([n, path]);
                        if (n?.type === type && path.length > 1) {
                            const parent = (editor as any).api?.parent?.(path);
                            const parentType = parent?.[0]?.type;
                            if (parentType === 'column' || parentType === type) {
                                (editor as any).tf.unwrapNodes({ at: path });
                                return;
                            }
                        }
                        return normalizeNode([n, path]);
                    },
                },
            }))
            .withComponent(ColumnGroupElement),
        // Auto-assign column IDs so drag-resize survives reloads.
        (ColumnItemPlugin as any)
            .overrideEditor(({ editor, tf: { normalizeNode }, type }: any) => ({
                transforms: {
                    normalizeNode([n, path]: any) {
                        // Body only acts when n.type === type (column item). Skip everything else.
                        if (n?.type !== type) return normalizeNode([n, path]);
                        if (n?.type === type && !n.id) {
                            const id = `col-${Math.random().toString(36).slice(2, 10)}`;
                            (editor as any).tf.setNodes({ id }, { at: path });
                            return;
                        }
                        return normalizeNode([n, path]);
                    },
                },
            }))
            .withComponent(ColumnElement),
        // Tables/<hr> are void (no caret inside); TrailingBlockPlugin only fixes
        // the document TAIL, so two adjacent voids elsewhere have no caret target
        // between them. Interior-only scope (never touches the last node) so this
        // can't fight TrailingBlock.
        (TablePlugin as any)
            // Declare the Excel-style single-cell selection option so the cell
            // renderer has store state to subscribe to (see cellSelection.ts).
            //
            // disableMerge: the app ships no merge UI (cells are unmerged on
            // load; "Split cell" works off colSpan/rowSpan and is not
            // merge-gated). Leaving merge enabled made the library's
            // useTableMergeState register three editor-wide selector
            // subscriptions PER CELL — one allocating a fresh [] on every
            // editor change — so every mounted cell re-rendered on every
            // keystroke/caret move anywhere in the doc.
            .configure({ options: { [CELL_SELECTION_OPTION]: null, disableMerge: true } })
            .overrideEditor(({ editor, tf: { normalizeNode, deleteBackward, deleteForward } }: any) => ({
                transforms: {
                    // Block Backspace/Delete from merging an adjacent top-level
                    // block INTO a table/void cell (which would drag the caret —
                    // and any text to the right — inside the last cell). Only the
                    // boundary-crossing case is short-circuited; every other
                    // delete (inside cells, joining paragraphs, char deletion)
                    // runs normally. See preventTableBoundaryMerge.ts.
                    deleteBackward(unit: any) {
                        if (shouldPreventTableBoundaryMerge(editor, 'backward')) return;
                        deleteBackward(unit);
                    },
                    deleteForward(unit: any) {
                        if (shouldPreventTableBoundaryMerge(editor, 'forward')) return;
                        deleteForward(unit);
                    },
                    normalizeNode([n, path]: any) {
                        // Body only acts on the document root (path.length === 0). Skip all other nodes.
                        if (path.length !== 0) return normalizeNode([n, path]);
                        if (Array.isArray(path) && path.length === 0 && Array.isArray(n?.children)) {
                            const insertAt = findInterVoidInsertIndex(n.children);
                            if (insertAt != null) {
                                (editor as any).tf.insertNodes(emptyParagraph(), { at: [insertAt] });
                                return;
                            }
                        }
                        return normalizeNode([n, path]);
                    },
                },
            }))
            .withComponent(TableElement),
        (TableRowPlugin as any).withComponent(TableRowElement),
        (TableCellPlugin as any).overrideEditor(withCellIdNormalizer).withComponent(TableCellElement),
        (TableCellHeaderPlugin as any).overrideEditor(withCellIdNormalizer).withComponent(TableCellHeaderElement),
        // Find & Replace — decoration-only. The base FindReplacePlugin only
        // DECORATES matches (search_highlight: true); it ships no component, so
        // withComponent(SearchHighlightLeaf) is required to actually paint them.
        // Highlights are cleared when the panel closes; serializers never see them
        // because decorations exist only in the React render pass and are not
        // stored in the editor value.
        (FindReplacePlugin as any).withComponent(SearchHighlightLeaf),
        // Table of contents block — void node that auto-derives the heading list
        // from the document and renders clickable scroll targets. isScroll:false
        // tells the plugin to use window.scrollTo rather than a container ref
        // (the editor has no constrained scroll container).
        (TocPlugin as any).configure({ options: { isScroll: false } }).withComponent(TocElement),
        // MarkdownPlugin is required for deserializeMd (used by markdownToPlate /
        // AI menu / ImportDocxButton markdown path). Without it, deserializeMd
        // falls back to a default rule set that cannot route through our plugin
        // configuration (headings, table, link, etc.), producing broken output.
        // Registered here so all editor instances have it — it has no visible
        // UI side-effect.
        MarkdownPlugin as any,
        TrailingBlockPlugin,
        ExitBreakPlugin,
        // Code block with lowlight syntax highlighting.
        // CodeSyntaxPlugin is a sub-plugin of CodeBlockPlugin; registered
        // explicitly so withComponent wiring is isolated per type.
        (CodeBlockPlugin as any).configure({ options: { lowlight, defaultLanguage: 'plaintext' } }).withComponent(CodeBlockElement),
        (CodeLinePlugin as any).withComponent(CodeLineElement),
        CodeSyntaxPlugin as any,
    ];
}

export function usePlateEditorLogic({ initialValue, enableMentions = false, enableBlockDnd = false }: UsePlateEditorLogicArgs = {}) {
    // usePlateEditor consumes these only at first creation, but without memoizing
    // they'd rebuild on every host re-render — ~40 plugin objects plus a full
    // initial-value sanitize walk — for nothing. Memoize so that cost is paid once.
    const plugins = useMemo(() => createPlateEditorPlugins({ enableMentions, enableBlockDnd }), [enableMentions, enableBlockDnd]);
    const value = useMemo(() => sanitizePlateValue(initialValue ?? EMPTY_EDITOR_VALUE), [initialValue]);
    const editor = usePlateEditor({ plugins, value });
    return { editor };
}
